// Dashboard entry point. Wires the static chrome (tabs, drawer), restores the
// view/metric/open-session from the URL hash, then kicks off the async loads.
// Lands on the Highlights tab by default (when the hash is empty); an explicit
// deep link to any other view wins. Bundled by tsup into dist/client/app.js and
// loaded by index.html.
import { state, $, esc, get, getStatus, post, dayOf } from './core'
import { renderAuthGate } from './auth'
import { initSettings, showSettings } from './settings'
import { initRouter, withoutSync, buildHash } from './router'
import { loadFacets } from './facets'
import { loadKpis, paintKpis, renderWindow, renderOpenMetric } from './kpis'
import { loadFilterBar, mountScopeBar, onScopeChange } from './filterbar'
import { renderSrControls, loadSuccessRate } from './metrics/successRate'
import { renderHighlights, paintHighlights, paintDashAsk, goHighlights, refreshHighlightsIfStale, setInsightsSub } from './home'
import { renderNotices } from './notice'
import { clearAsked } from './askbanner'
import { buildFilters, closeDrawer, setView, openDetail, applySessionParams } from './sessions'
import { renderArtKindSeg, loadArtifacts } from './artifacts'
import { refreshInbox } from './inbox'
import { renderBenchmarks, applyBenchParams } from './benchmarks'
import { renderTaskReplay, applyTrParams } from './task-replay'
import { applyReplayParams, renderReplay } from './replay/index'
import { renderSkills, applySkillParams } from './skills'
import { renderTools, applyToolParams } from './tools'
import { renderInsights } from './insights'
import { openBugDrawer, closeBugDrawer } from './metrics/defectRate'
import { renderModels } from './models'

// The one post-boot metric repaint. The open metric pre-renders before the two
// async payloads it depends on — facets (its breakdown controls) and /api/filters
// (the role-gated team UI) — arrive. They used to repaint INDEPENDENTLY, so a
// dashboard boot ran the metric's whole fetch fan-out three (with the smart
// kind-flip, four) times for one visual change. Each arm now just releases its
// gate; the render fires ONCE, when both are in.
var bootReady = { facets: false, filters: false, done: false };
function repaintMetricWhenReady(which) {
  bootReady[which] = true;
  if (bootReady.done || !bootReady.facets || !bootReady.filters) return;
  bootReady.done = true;
  // Facets drive the breakdown dropdown AND the per-value table's facet-named
  // title/labels; filters drive the role gates. Success-rate has dedicated
  // facet-aware controls; every other metric just re-renders.
  if (state.metric === 'success_rate') { renderSrControls(); loadSuccessRate(); }
  else renderOpenMetric();
}

function boot(me) {
  state.me = me; // current user, for owner "(you)" labels etc.
  renderUserChip(me);
  refreshInbox(); // populate the access-request badge (hidden when there are none)
  initSettings(me); // give the settings pane the current user (name prefill, role)
  // Attach Back/Forward + hash-edit listeners and read where the URL says to land.
  var route = initRouter();

  // The drawer's close button is rendered per-open inside the sticky header
  // (wired in openDetail); the overlay click still closes from anywhere.
  $('#overlay').onclick = function () { closeDrawer(); closeBugDrawer(); };
  Array.prototype.forEach.call(document.querySelectorAll('.tab'), function (b) {
    // Manual tab nav drops any question-grounding banner. Settings renders lazily
    // the first time it's shown.
    b.onclick = function () {
      clearAsked();
      var v = b.getAttribute('data-view');
      setView(v);
      if (v === 'settings') showSettings();
      if (v === 'skills') renderSkills(); // on-demand: scope-dependent, so not pre-rendered
      if (v === 'tools') renderTools(); // on-demand: scope-dependent
      if (v === 'insights') {
        setInsightsSub(state.insightsSub || 'highlights');
        if (state.insightsSub === 'recommendations') renderInsights();
        else refreshHighlightsIfStale();
      }
      if (v === 'models') renderModels(); // on-demand: scope-dependent
      if (v === 'replay') renderReplay(); // on-demand: fetched when first shown
      // The boot pre-render's fetch only paints if Task Replay is the active view
      // when it lands, so tabbing in later must render (else "Loading…" sticks).
      if (v === 'taskreplay') renderTaskReplay();
      // The scope bar is a single active mount shared across tabs; re-anchor it to
      // the Dashboard's container on the way in so a scope change made on Skills
      // (which moved the mount) can't leave the Dashboard's selects stale.
      if (v === 'dashboard') {
        var fb = document.getElementById('filter-bar');
        if (fb) mountScopeBar(fb, onScopeChange);
      }
    };
  });

  Array.prototype.forEach.call(document.querySelectorAll('#insights-subtabs .subtab'), function (b) {
    b.onclick = function () {
      var sub = b.getAttribute('data-sub');
      setInsightsSub(sub);
      if (sub === 'recommendations') renderInsights();
      else refreshHighlightsIfStale();
    };
  });

  // Insights (Highlights subtab) is the landing tab: an empty/partial hash lands
  // there, while a deep link to any other view wins.
  var hash = window.location.hash;
  var landInsights = !hash || hash === '#' || hash === '#/';

  // Initial paint driven by the URL. Wrapped in withoutSync so none of these nav
  // calls mint history entries; the single replaceState below normalizes the URL
  // (so an empty or partial hash becomes its canonical form).
  withoutSync(function () {
    state.artKind = route.artKind;
    state.metric = route.metric; // the KPI tile that's expanded (default cost_artifact)
    // Restore the artifacts table's search/sort only when we're landing there
    // (the query params are scoped to the active view).
    if (route.view === 'artifacts') {
      state.art = { q: route.query.q || '', sort: route.query.sort || 'created', dir: route.query.dir === 'asc' ? 'asc' : 'desc' };
    }
    renderArtKindSeg();
    renderOpenMetric(); // pre-render the chosen dashboard metric's detail
    renderHighlights(); // pre-render the Highlights tab so it's ready whether we land there or tab in later
    // Restore the benchmarks drill-in (bench/run/task) from the URL when landing
    // there; otherwise pre-render its top-level list so tabbing in is instant.
    if (route.view === 'benchmarks') applyBenchParams(route.query);
    else renderBenchmarks();
    // Skills renders on demand (its data is scope-dependent, so pre-rendering
    // before /api/filters seeds the scope would fetch twice); restore the
    // drill-in only when landing there.
    if (route.view === 'skills') applySkillParams(route.query);
    if (route.view === 'tools') applyToolParams(route.query);
    if (route.view === 'models') renderModels();
    // Task Replay: restore the run-detail drill-in from the URL when landing
    // there; otherwise pre-render the list so tabbing in is instant.
    if (route.view === 'taskreplay') applyTrParams(route.query);
    else renderTaskReplay();
    if (route.view === 'replay') applyReplayParams(route.query);
    setView(landInsights ? 'insights' : route.view);
    if (landInsights || route.view === 'insights') setInsightsSub(route.insightsSub || 'highlights');
    if (!landInsights && route.view === 'settings') showSettings(); // deep link to Settings
    if (route.session) openDetail(route.session); // deep-linked drawer
    else if (route.bug) openBugDrawer(route.bug); // deep-linked defect drawer
  });

  // The brand logo returns to Highlights from anywhere.
  var brand = document.querySelector('.brand') as HTMLElement | null;
  if (brand) { brand.style.cursor = 'pointer'; brand.onclick = goHighlights; }

  get('/api/overview').then(function (o) {
    state.overview = o;
    var range = o.firstAt && o.lastAt ? dayOf(o.firstAt) + ' → ' + dayOf(o.lastAt) : '';
    // The header shows the coverage of what's been ingested. There is no store
    // path or scanned-directory list any more: transcripts are pushed to this
    // server rather than discovered on one developer's disk, so per-directory
    // scan provenance is not a thing the dashboard can (or should) report.
    var info = '';
    if (range || o.lastAnalyzedAt) {
      var head =
        (range ? '<div class="mi-head">Sessions ' + esc(range) + '</div>' : '') +
        (o.lastAnalyzedAt ? '<div class="mi-head">Last analyzed ' + esc(dayOf(o.lastAnalyzedAt)) + '</div>' : '');
      info = '<span class="meta-info-wrap">' +
        '<button type="button" class="meta-info-btn" id="metaInfoBtn" title="Ingest details" aria-label="Ingest details">&#9432;</button>' +
        '<div class="meta-info-pop" id="metaInfoPop">' + head + '</div></span>';
    }
    $('#meta').innerHTML =
      '<span class="meta-top">' + info + '</span>';
    var miBtn = document.getElementById('metaInfoBtn');
    if (miBtn) {
      miBtn.onclick = function (e) {
        e.stopPropagation();
        var pop = document.getElementById('metaInfoPop');
        if (pop) pop.classList.toggle('on');
      };
      // Close on an outside click (same pattern as the outcome popover).
      document.addEventListener('mousedown', function (e) {
        var pop = document.getElementById('metaInfoPop');
        if (!pop || !pop.classList.contains('on')) return;
        var wrap = document.querySelector('.meta-info-wrap');
        if (wrap && wrap.contains(e.target as Node)) return;
        pop.classList.remove('on');
      });
    }
    // The overview is what classifies the store (empty / un-enriched / ok) and
    // whether any outcomes exist, so surface the nudges + correct the outcome-rate
    // tile now that it's known (these paint from cached payloads — no refetch).
    renderNotices();
    paintKpis();
    paintHighlights();
    paintDashAsk();
    loadFacets().then(function () {
      // Build the sessions filter bar. If we landed on the sessions view, restore
      // its filter from the URL (facets are needed to populate the selects, so this
      // waits for them); otherwise build the default bar.
      if (route.view === 'sessions') applySessionParams(route.query);
      else buildFilters();
      repaintMetricWhenReady('facets');
    });
  });
  renderWindow();
  // Load the team/repo filter options + seed the default scope BEFORE the first
  // KPI load, so a member's initial dashboard is already scoped to their team
  // rather than flashing global then re-scoping.
  // Also repaints Highlights: its first fetch (above) raced ahead of /api/filters,
  // so its scope control and scope-aware copy couldn't render yet. A repaint, not
  // a refetch — the unparameterized first fetch already resolved to exactly the
  // role default the seeded scope means (lead → own team, admin → global, member
  // → self), so only the labels were missing, never the numbers.
  loadFilterBar().then(function () {
    loadKpis();
    paintHighlights();
    // A deep link straight to Skills/Tools painted before /api/filters resolved,
    // so its scope bar had nothing to offer yet — repaint now that the options are in.
    if (state.view === 'skills') renderSkills();
    if (state.view === 'tools') renderTools();
    if (state.view === 'insights' && state.insightsSub === 'recommendations') renderInsights();
    repaintMetricWhenReady('filters');
  }, function () {
    // /api/filters failing must not strand the metric on its pre-render:
    // release the gate so the facets arm still repaints (without team UI).
    repaintMetricWhenReady('filters');
  });
  // Sessions load via buildFilters() → applyFilters() once facets resolve, so the
  // list arrives already windowed to the default (30d) with its active chips.
  loadArtifacts();
  // Unparameterized like the first Highlights fetch (same race, same argument):
  // this fires before /api/filters seeds state.scope, and the server resolves a
  // scope-less request to the role default the seed will mean anyway (member →
  // self, lead → own team, admin → global). Scope CHANGES refetch with scopeQs()
  // (onScopeChange), keeping the counts in step with the charts.
  get('/api/outcome-types').then(function (t) {
    state.outcomeTypes = t || [];
    if (state.metric === 'success_rate') renderSrControls();
  });

  // Collapse the (possibly empty / partial) initial hash to its canonical form,
  // in place — no extra history entry beyond the page load itself.
  window.history.replaceState(null, '', buildHash());
}

function renderUserChip(me) {
  var slot = $('#user');
  if (!slot) return;
  var who = esc(me.name || me.email);
  var admin = me.role === 'admin' ? '<span class="user-role">admin</span>'
    : me.role === 'team-lead' ? '<span class="user-role">team lead</span>' : '';
  slot.innerHTML = '<span class="user-name" title="' + esc(me.email) + '">' + who + '</span>' + admin +
    '<button type="button" class="user-settings" id="settingsBtn" title="Settings" aria-label="Settings">' +
      '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M8.3 2.6a1 1 0 0 1 1-.8h1.4a1 1 0 0 1 1 .8l.2 1.5a6.5 6.5 0 0 1 1.6.9l1.4-.6a1 1 0 0 1 1.2.4l.7 1.2a1 1 0 0 1-.2 1.2l-1.1 1a6.5 6.5 0 0 1 0 1.8l1.1 1a1 1 0 0 1 .2 1.2l-.7 1.2a1 1 0 0 1-1.2.4l-1.4-.6a6.5 6.5 0 0 1-1.6.9l-.2 1.5a1 1 0 0 1-1 .8H9.3a1 1 0 0 1-1-.8l-.2-1.5a6.5 6.5 0 0 1-1.6-.9l-1.4.6a1 1 0 0 1-1.2-.4l-.7-1.2a1 1 0 0 1 .2-1.2l1.1-1a6.5 6.5 0 0 1 0-1.8l-1.1-1a1 1 0 0 1-.2-1.2l.7-1.2a1 1 0 0 1 1.2-.4l1.4.6a6.5 6.5 0 0 1 1.6-.9l.2-1.5Z"/>' +
        '<circle cx="10" cy="10" r="2.5"/>' +
      '</svg>' +
    '</button>';
  var btn = document.getElementById('settingsBtn');
  if (btn) btn.onclick = function () {
    clearAsked();
    setView('settings');
    showSettings();
  };
}

// Gate the whole app on the session: /api/auth/me answers who is signed in
// (200 + identity) or not (401). Branch on the HTTP status so a transient
// server/network failure isn't misread as "logged out": only an outage (5xx,
// network) shows the retryable error; a 401 — or any answer that isn't a usable
// identity — shows the login screen. (The guard hook also protects every data
// endpoint server-side — this is the UX gate, not the security boundary.)
getStatus('/api/auth/me').then(function (r) {
  if (r.status >= 200 && r.status < 300 && r.body && r.body.id) boot(r.body);
  else if (r.status >= 500) renderBootError();
  else renderAuthGate('login');
}).catch(function () { renderBootError(); });

// A distinct state for "couldn't reach the server", so a 500/outage doesn't
// masquerade as a login prompt the user then can't get past.
function renderBootError() {
  var gate = $('#auth-gate');
  if (!gate) return;
  gate.innerHTML = '<div class="auth-card"><div class="auth-brand">tuneloop</div>' +
    '<div class="auth-title">Couldn’t reach the server</div>' +
    '<button class="auth-btn" type="button" id="bootRetry">Retry</button></div>';
  gate.classList.add('on');
  var retry = document.getElementById('bootRetry');
  if (retry) retry.onclick = function () { window.location.reload(); };
}
